<!--
  Export — Excel and PDF per pour, Excel for the whole-job roll-up. Crew AND client.

  Spec, success criteria: "Any pour's documents can be handed to a client in
  under a minute." Nothing on this panel is gated on `canEdit`: a client session
  reaches every route here, because handing a client their records without
  emailing files around is the app's stated secondary purpose.

  Every spreadsheet on this panel is an Excel workbook, and every one of them is
  built from the same blocks its CSV is built from, so the two cannot drift
  apart. The CSV routes are still there for anything automated; the buttons that
  pointed at them are not.

  Excel and not CSV because the client asked for the images IN the spreadsheet.
  A CSV cannot do that: it has one cell type, text. The workbook embeds each
  photograph under a caption carrying everything a manifest row would have said,
  and a click on one opens the full-size original through the app's own
  authenticated photo route.

  The whole-job workbook is the one that grows with the job, so it carries a
  size budget: past it, photographs are listed with their links rather than
  their pictures, and the sheet says so. See PROJECT_EMBED_LIMITS.

  The PDF is one document reached two ways, and the order they are offered in is
  deliberate:

    1. **Print / Save as PDF** builds the print-styled document in
       `../lib/export/pdf.ts` and hands it to the browser's own print engine.
       That is the good one — real text layout, repeating table headers, proper
       page breaks — and it ships no PDF library, which matters on site WiFi.
    2. **Download PDF** hits the API and gets a FILE: the Worker redraws that
       same print-styled document as a real PDF and the browser saves it. One
       click, no dialog, nothing opened in a tab. It is what a client clicks
       from a share link on a phone, and the fallback for a device that will
       not print.

  The panel is a plain `<details>` block, never a dialog: the spec forbids
  anything that can trap a user mid-pour, and a client waiting on a download is
  exactly when that would happen.
-->
<script lang="ts">
  import { exportUrl } from '../lib/api';
  import { printPourRecord } from '../lib/export/pdf';
  import { currentPour, isCrew, loadPour, notify, project, shiftName } from '../lib/store';
  import type { ItpItem, PourFull, StopHoldEvent } from '../lib/types';

  interface Props {
    /** The pour to export. Falls back to the pour the app currently has open. */
    pour?: PourFull | null;
    /** Used to fetch the record when only an id is to hand (e.g. deep link). */
    pourId?: number | null;
    /** Start expanded. The pour screen collapses it; a dedicated screen would not. */
    open?: boolean;
  }

  let { pour = null, pourId = null, open = false }: Props = $props();

  let printing = $state(false);
  let fetching = $state(false);

  /** The record to export: the prop, else whatever the app has loaded. */
  const record = $derived<PourFull | null>(pour ?? $currentPour);
  const targetId = $derived<number | null>(record?.pour.id ?? pourId);

  /**
   * Failed Hold points that are not yet paired with a resolved stop-hold event.
   *
   * Mirrors `findBlockingHoldPoints` in the API exactly — same definition of
   * "resolved" (a corrective action AND a restart time), same pairing by count
   * in item order — so the banner printed on an exported record agrees with the
   * reason the server would give for refusing to close the pour.
   */
  function blockingHoldPoints(full: PourFull): ItpItem[] {
    const failed = full.itp_items
      .filter((item) => item.status === 'failed' && item.point_type.toUpperCase().includes('H'))
      .sort((a, b) => a.item_no - b.item_no);
    if (failed.length === 0) return [];

    const isResolved = (event: StopHoldEvent): boolean =>
      !!event.corrective_action?.trim() && !!event.restart_time?.trim();
    const paired = full.stop_hold_events.filter(isResolved).length;
    return failed.slice(paired);
  }

  /** Make sure we have the whole record before building a document from it. */
  async function ensureRecord(): Promise<PourFull | null> {
    if (record) return record;
    if (targetId === null) return null;
    fetching = true;
    try {
      return await loadPour(targetId);
    } finally {
      fetching = false;
    }
  }

  async function printRecord() {
    if (printing) return;
    printing = true;
    try {
      const full = await ensureRecord();
      const proj = $project;
      if (!full || !proj) {
        notify({
          tone: 'warn',
          title: 'Nothing to print yet',
          detail: 'The pour record has not finished loading.'
        });
        return;
      }

      const started = printPourRecord(
        {
          project: {
            code: proj.code,
            name: proj.name,
            client: proj.client,
            // Job defaults, so the printed document states the product, unit
            // and acceptance point the job runs to.
            product: proj.product,
            sci_unit_no: proj.sci_unit_no,
            acceptance_point: proj.acceptance_point
          },
          pour: full,
          exported_at: new Date().toISOString(),
          exported_by: $shiftName,
          blocking_hold_points: blockingHoldPoints(full)
        },
        {
          onDone: (error) => {
            printing = false;
            if (error) {
              notify({
                tone: 'warn',
                title: 'Could not open the print view',
                detail: 'Use "Download PDF" instead — it produces the same record.',
                sticky: true
              });
            }
          }
        }
      );

      if (!started) {
        printing = false;
        notify({
          tone: 'warn',
          title: 'This browser will not print',
          detail: 'Use "Download PDF" instead — it produces the same record.',
          sticky: true
        });
      }
    } catch (error) {
      printing = false;
      notify({
        tone: 'error',
        title: 'Could not build the document',
        detail: error instanceof Error ? error.message : String(error),
        sticky: true
      });
    }
  }

  const pourName = $derived(record ? `${record.pour.label} — ${record.pour.date}` : '');
</script>

<details class="section" {open}>
  <summary>
    <span class="section__label">Export</span>
    <span class="pill pill--pending">CSV · Excel · PDF</span>
  </summary>

  <div class="section__body stack">
    {#if targetId !== null}
      <div>
        <p class="label">This pour{#if pourName} — {pourName}{/if}</p>
        <div class="btnrow">
          <button
            class="btn btn--primary"
            type="button"
            disabled={printing || fetching}
            onclick={printRecord}
          >
            {#if printing || fetching}
              <span class="spinner" aria-hidden="true"></span> Preparing…
            {:else}
              Print / Save as PDF
            {/if}
          </button>
          <!--
            No per-pour CSV button. The owner cut it 2026-08-31 — Excel carries
            every table the CSV does PLUS the photographs, so two buttons were
            one button with a worse twin. The /export.csv route itself remains
            for anything automated; only the control is gone. The whole-job
            roll-up below stays CSV until it earns an Excel counterpart.
          -->
          <a class="btn" href={exportUrl.pourXlsx(targetId)} download>Download Excel</a>
          <a class="btn" href={exportUrl.pourPdf(targetId)} download>Download PDF</a>
        </div>
        <p class="meta">
          The Excel copy holds the header, calibration, chute-test and stop-hold tables, the ITP
          and end-of-pour checklists, and the photographs themselves in the sheet — click a
          picture to open the full-size original.
        </p>
      </div>
    {/if}

    <div>
      <p class="label">Whole job</p>
      <div class="btnrow">
        <!--
          No whole-job CSV button either, for the reason the per-pour one went:
          the workbook carries every table the CSV does PLUS the photographs.
          The /export/pours.csv route stays for anything automated.
        -->
        <a class="btn" href={exportUrl.projectXlsx()} download>All pours (Excel)</a>
      </div>
      <p class="meta">
        Every pour on the job, plus every chute test and stop-hold event across all of them, each
        row stamped with the pour it belongs to — and the photographs from every pour, in the
        sheet. On a long job the newest pours keep their pictures and older ones are listed with
        links instead, so the file stays a size you can open; the sheet says when that has
        happened, and that pour’s own export still carries them all.
      </p>
    </div>

    <p class="meta note">
      {#if $isCrew}
        Clients can download exactly these files themselves from a client link — there is no need
        to email anything.
      {:else}
        These files are yours to keep. They are generated from the live record each time you ask
        for them.
      {/if}
    </p>
  </div>
</details>

<style>
  .label {
    font-weight: 600;
    margin-bottom: 0.4rem;
  }

  .btnrow {
    margin-bottom: 0.4rem;
  }

  .note {
    border-top: 1px solid var(--line-soft);
    padding-top: 0.6rem;
  }
</style>
